import * as React from 'react';
import { X } from 'lucide-react';

/**
 * Drawer side anchor
 */
export type DrawerSide = 'left' | 'right';

/**
 * Drawer named width variants
 * - sm: 320px (compact side panels, navigation)
 * - md: 400px (default, comments / inspector panes)
 * - lg: 560px (rich detail surfaces)
 */
export type DrawerWidthVariant = 'sm' | 'md' | 'lg';

/**
 * Drawer width prop — accepts a named variant, a number (treated as px),
 * or any CSS length string (e.g. `"60vw"`).
 */
export type DrawerWidth = DrawerWidthVariant | number | string;

const namedWidths: Record<DrawerWidthVariant, number> = {
  sm: 320,
  md: 400,
  lg: 560,
};

function isNamedWidth(value: string): value is DrawerWidthVariant {
  return value === 'sm' || value === 'md' || value === 'lg';
}

function resolveWidth(width: DrawerWidth): string {
  if (typeof width === 'number') return `${width}px`;
  if (isNamedWidth(width)) return `${namedWidths[width]}px`;
  return width;
}

/**
 * Props for the Drawer component
 */
export interface DrawerProps {
  /**
   * Whether the drawer is open
   */
  open: boolean;
  /**
   * Callback when the drawer should close
   */
  onClose: () => void;
  /**
   * Which edge of the viewport the drawer slides in from
   * @default 'right'
   */
  side?: DrawerSide;
  /**
   * Drawer panel width
   * @default 'md'
   */
  width?: DrawerWidth;
  /**
   * Whether clicking the backdrop closes the drawer
   * @default true
   */
  closeOnBackdropClick?: boolean;
  /**
   * Whether pressing Escape closes the drawer
   * @default true
   */
  closeOnEscape?: boolean;
  /**
   * Drawer content (typically Drawer.Header, Drawer.Body, Drawer.Footer)
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes for the drawer panel
   */
  className?: string;
  /**
   * ID for the drawer title element (for aria-labelledby)
   */
  'aria-labelledby'?: string;
  /**
   * ID for the drawer description element (for aria-describedby)
   */
  'aria-describedby'?: string;
}

/**
 * Props for Drawer.Header
 */
export interface DrawerHeaderProps {
  /**
   * Title text for the drawer header
   */
  title?: string;
  /**
   * Whether to show the close button
   * @default true
   */
  showCloseButton?: boolean;
  /**
   * Additional content to render in the header (left of the close button)
   */
  children?: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Props for Drawer.Body
 */
export interface DrawerBodyProps {
  /**
   * Body content
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Props for Drawer.Footer
 */
export interface DrawerFooterProps {
  /**
   * Footer content (typically action buttons or a sticky composer)
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

interface DrawerContextValue {
  onClose: () => void;
  titleId: string;
}

const DrawerContext = React.createContext<DrawerContextValue | null>(null);

function useDrawerContext(): DrawerContextValue {
  const context = React.useContext(DrawerContext);
  if (!context) {
    throw new Error('Drawer compound components must be used within Drawer');
  }
  return context;
}

function DrawerHeader({
  title,
  showCloseButton = true,
  children,
  className = '',
}: Readonly<DrawerHeaderProps>) {
  const { onClose, titleId } = useDrawerContext();

  return (
    <div
      className={[
        'flex items-center justify-between',
        'min-h-[60px]',
        'px-6 py-3',
        'border-b border-secondary-200',
        className,
      ].filter(Boolean).join(' ')}
    >
      <div className="flex-1 min-w-0">
        {title && (
          <h2
            id={titleId}
            className="text-lg font-semibold text-secondary-800 truncate"
          >
            {title}
          </h2>
        )}
        {children}
      </div>
      {showCloseButton && (
        <button
          type="button"
          onClick={onClose}
          className="flex-shrink-0 ml-4 p-1 rounded hover:bg-black/5 transition-colors duration-150 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-200"
          aria-label="Close drawer"
        >
          <X
            className="w-5 h-5 text-secondary-400 hover:text-secondary-600"
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
}

DrawerHeader.displayName = 'Drawer.Header';

function DrawerBody({ children, className = '' }: Readonly<DrawerBodyProps>) {
  return (
    <div
      className={[
        'flex-1 overflow-y-auto',
        'p-6',
        className,
      ].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );
}

DrawerBody.displayName = 'Drawer.Body';

function DrawerFooter({ children, className = '' }: Readonly<DrawerFooterProps>) {
  return (
    <div
      className={[
        'flex items-center gap-3',
        'min-h-[60px]',
        'px-6 py-3',
        'border-t border-secondary-200',
        className,
      ].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );
}

DrawerFooter.displayName = 'Drawer.Footer';

let drawerIdCounter = 0;
function generateDrawerId(): string {
  return `drawer-${Date.now()}-${++drawerIdCounter}`;
}

/**
 * Drawer — right-anchored (or left-anchored) overlay panel.
 *
 * Mirrors Modal's API and a11y guarantees: focus trap, scroll lock, Escape
 * dismissal, dialog role + aria-modal. Designed for inspector / details
 * surfaces (comments, history, filters) where the user needs to skim a
 * sidebar without losing the main view's context.
 *
 * @example
 * <Drawer open={open} onClose={() => setOpen(false)} width="md">
 *   <Drawer.Header title="Commentaires" />
 *   <Drawer.Body>{listOfComments}</Drawer.Body>
 *   <Drawer.Footer>{composer}</Drawer.Footer>
 * </Drawer>
 */
const DrawerRoot = React.forwardRef<HTMLDialogElement, DrawerProps>(
  (
    {
      open,
      onClose,
      side = 'right',
      width = 'md',
      closeOnBackdropClick = true,
      closeOnEscape = true,
      children,
      className = '',
      'aria-labelledby': ariaLabelledby,
      'aria-describedby': ariaDescribedby,
    },
    ref,
  ) => {
    const drawerRef = React.useRef<HTMLDialogElement>(null);
    const previousActiveElement = React.useRef<HTMLElement | null>(null);
    const [isExiting, setIsExiting] = React.useState(false);
    const [titleId] = React.useState(() => ariaLabelledby || generateDrawerId());

    React.useImperativeHandle(ref, () => drawerRef.current as HTMLDialogElement);

    const handleClose = React.useCallback(() => {
      setIsExiting(true);
      setTimeout(() => {
        setIsExiting(false);
        onClose();
      }, 200);
    }, [onClose]);

    React.useEffect(() => {
      if (!open || !closeOnEscape) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          handleClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, closeOnEscape, handleClose]);

    React.useEffect(() => {
      if (!open) return;

      const drawer = drawerRef.current;
      /* v8 ignore start */
      if (!drawer) return;
      /* v8 ignore stop */

      previousActiveElement.current = document.activeElement as HTMLElement;

      const getFocusableElements = () =>
        drawer.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );

      const focusableElements = getFocusableElements();
      const firstElement = focusableElements[0];
      if (firstElement) {
        firstElement.focus();
      } else {
        drawer.focus();
      }

      const handleTabKey = (event: KeyboardEvent) => {
        if (event.key !== 'Tab') return;

        const focusable = getFocusableElements();
        if (focusable.length === 0) return;

        const firstFocusable = focusable[0];
        const lastFocusable = focusable[focusable.length - 1];

        /* v8 ignore start */
        if (!firstFocusable || !lastFocusable) return;
        /* v8 ignore stop */

        if (event.shiftKey && document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        } else if (!event.shiftKey && document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      };

      document.addEventListener('keydown', handleTabKey);
      return () => document.removeEventListener('keydown', handleTabKey);
    }, [open]);

    React.useEffect(() => {
      if (!open && previousActiveElement.current) {
        previousActiveElement.current.focus();
        previousActiveElement.current = null;
      }
    }, [open]);

    React.useEffect(() => {
      if (open) {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
          document.body.style.overflow = originalOverflow;
        };
      }
    }, [open]);

    const contextValue = React.useMemo<DrawerContextValue>(
      () => ({ onClose: handleClose, titleId }),
      [handleClose, titleId],
    );

    if (!open && !isExiting) return null;

    const sideAnchor = side === 'right' ? 'right-0 left-auto' : 'left-0 right-auto';
    const enterAnim = side === 'right' ? 'animate-drawer-enter-right' : 'animate-drawer-enter-left';
    const exitAnim = side === 'right' ? 'animate-drawer-exit-right' : 'animate-drawer-exit-left';
    const animationClass = isExiting ? exitAnim : enterAnim;

    return (
      <div className="fixed inset-0 z-50">
        <button
          type="button"
          className={[
            'absolute inset-0 w-full h-full',
            'border-none cursor-default',
            isExiting ? 'animate-modal-backdrop-exit' : 'animate-modal-backdrop-enter',
          ].join(' ')}
          style={{ backgroundColor: 'rgba(28, 25, 23, 0.5)' }}
          onClick={closeOnBackdropClick ? handleClose : undefined}
          aria-label="Close drawer by clicking backdrop"
          tabIndex={-1}
        />
        <dialog
          ref={drawerRef}
          open
          aria-modal="true"
          aria-labelledby={ariaLabelledby || titleId}
          aria-describedby={ariaDescribedby}
          tabIndex={-1}
          data-side={side}
          style={{ width: resolveWidth(width), maxWidth: '100vw' }}
          className={[
            'absolute top-0 bottom-0 m-0 p-0',
            sideAnchor,
            'h-screen',
            'flex flex-col',
            'bg-secondary-100',
            'shadow-xl',
            animationClass,
            'motion-reduce:animate-none',
            className,
          ].filter(Boolean).join(' ')}
        >
          <DrawerContext.Provider value={contextValue}>
            {children}
          </DrawerContext.Provider>
        </dialog>
      </div>
    );
  },
);

DrawerRoot.displayName = 'Drawer';

export const Drawer = Object.assign(DrawerRoot, {
  Header: DrawerHeader,
  Body: DrawerBody,
  Footer: DrawerFooter,
});
